import React from 'react';
import SEOHead from './SEOHead';

interface SiteMetadataProps {
  page: 'home' | 'mentorship' | 'chip' | string;
  title?: string;
  description?: string;
  keywords?: string[];
}

const SiteMetadata: React.FC<SiteMetadataProps> = ({
  page,
  title,
  description,
  keywords = []
}) => {
  // Default metadata
  const defaultTitle = 'DetailerOS - Transform Your Detailing Business';
  const defaultDescription = 'Expert coaching and powerful software to help detailing professionals scale their businesses.';
  
  // Common keywords across the site
  const commonKeywords = [
    'detailing business', 
    'auto detailer', 
    'car detailing', 
    'detail shop management', 
    'detailing software', 
    'auto detail business'
  ];
  
  // Base URL for canonical links and images
  const baseUrl = 'https://detaileros.com'; // Replace with your actual domain
  const ogImage = `${baseUrl}/og-image-${page}.jpg`; // Replace with actual OG images
  
  // Combine provided keywords with common keywords
  const allKeywords = [...keywords, ...commonKeywords];
  
  // JSON-LD structured data for business
  const businessJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "DetailerOS",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "147.00",
      "priceCurrency": "USD"
    },
    "description": "All-in-one system for detailing businesses. Expert coaching, technology platforms, and marketing solutions.",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "250"
    }
  };
  
  // Page-specific metadata and structured data
  let pageMetadata = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    canonicalUrl: '/',
    keywords: allKeywords,
    ogImage,
    jsonLd: businessJsonLd
  };
  
  if (page === 'mentorship') {
    const mentorshipJsonLd = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "DetailerOS Business Mentorship",
      "serviceType": "Business Coaching",
      "description": "Expert mentorship for auto detailing business owners to scale operations and increase profitability.",
      "provider": {
        "@type": "Organization",
        "name": "DetailerOS"
      },
      "offers": {
        "@type": "Offer",
        "price": "347.00",
        "priceCurrency": "USD"
      }
    };
    
    pageMetadata = {
      title: title || 'DetailerOS Business Mentorship - Expert Coaching for Detailers',
      description: description || 'Transform your detailing business with expert mentorship and coaching. Learn proven strategies to scale your operations and increase profitability.',
      canonicalUrl: '/mentorship',
      keywords: [...allKeywords, 'detailing business coaching', 'auto detailing mentorship', 'detailing business consultant', 'detailing business growth'],
      ogImage: `${baseUrl}/og-image-mentorship.jpg`,
      jsonLd: mentorshipJsonLd
    };
  } 
  else if (page === 'chip') {
    const chipJsonLd = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "CHiP AI Assistant for Auto Detailers",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "AI-powered assistant specifically designed for auto detailing businesses to handle client inquiries and bookings 24/7.",
      "offers": {
        "@type": "Offer",
        "price": "347.00",
        "priceCurrency": "USD",
        "priceValidUntil": "2025-12-31"
      }
    };
    
    pageMetadata = {
      title: title || 'CHiP AI Assistant - 24/7 Sales Assistant for Detailers',
      description: description || 'CHiP is the industry-specific AI assistant designed to help detailing businesses capture leads, answer questions, and book appointments 24/7.',
      canonicalUrl: '/chip',
      keywords: [...allKeywords, 'ai for detailing business', 'auto detailing ai assistant', 'detailing business automation', 'car detailing lead generation'],
      ogImage: `${baseUrl}/og-image-chip.jpg`,
      jsonLd: chipJsonLd
    };
  }
  
  return <SEOHead {...pageMetadata} />;
};

export default SiteMetadata;